import { FieldMapValue, ShortcutArgs } from '../../types'
import { formatDollarValue } from '../data'
import { getDateString } from '../dates'
import { formatPhoneNumber } from '../fields'
import { getAddressValue } from './address'
import { getAllowedChargesString, getReimbursementString } from './cost'

const getNetworkValue = (path: string, data: FieldMapValue | undefined): string | undefined => {
  switch (path) {
    case 'deductible':
      return data?.deductible ? formatDollarValue(data.deductible) : undefined
    case 'coinsurance':
      return data?.coinsurance ? `${data.coinsurance}%` : undefined
    case 'oopMax':
      return data?.outOfPocketMax ? formatDollarValue(data.outOfPocketMax) : undefined
    default:
      return undefined
  }
}

const getPolicyOwnerValue = (path: string, shortcutArgs: ShortcutArgs): string | undefined => {
  const { assessment, user } = shortcutArgs ?? {}
  const { primaryCoverage } = assessment?.mergedData?.insuranceInfo ?? {}
  const { mergedSignOnData } = assessment ?? {}
  const policyOwnerRelationship =
    primaryCoverage?.policyOwnerRelationship ?? mergedSignOnData?.policyOwnerRelationship
  const patientNotPolicyOwner = policyOwnerRelationship !== 'self'

  const policyOwnerSameAddress =
    primaryCoverage?.policyOwner?.policyOwnerSameAddress ??
    mergedSignOnData?.policyOwnerInfo?.policyOwnerSameAddress

  const getPolicyOwnerValue = (path: string) =>
    primaryCoverage?.policyOwnerInfo?.[path] ?? mergedSignOnData?.policyOwnerInfo?.[path]

  const getPolicyOwnerNameValue = (path: string) =>
    primaryCoverage?.policyOwnerInfo?.policyOwnerName?.[path] ??
    mergedSignOnData?.policyOwnerInfo?.policyOwnerName?.[path]

  const address =
    patientNotPolicyOwner && !policyOwnerSameAddress
      ? getPolicyOwnerValue('policyOwnerAddress')
      : mergedSignOnData?.address

  const phone = patientNotPolicyOwner ? getPolicyOwnerValue('policyOwnerPhone') : user?.phone

  const dob = patientNotPolicyOwner ? getPolicyOwnerValue('policyOwnerDob') : user?.dob

  const sex = patientNotPolicyOwner ? getPolicyOwnerValue('policyOwnerSex') : 'F'
  switch (path) {
    case 'fname':
      return patientNotPolicyOwner ? getPolicyOwnerNameValue('fname') : (user?.fname ?? undefined)
    case 'lname':
      return patientNotPolicyOwner ? getPolicyOwnerNameValue('lname') : (user?.lname ?? undefined)
    case 'dob':
      return dob ? getDateString(dob, 'short') : undefined
    case 'address':
    case 'address2':
      return getAddressValue('address', path, address)
    case 'sex':
      return sex
    case 'phone':
      return phone ? formatPhoneNumber(phone) : undefined
    // return selectedCoverage?.policyOwner?.name
    default:
      return undefined
  }
}

const getInsurerValue = (path: string, shortcutArgs: ShortcutArgs): string | undefined => {
  const { plan } = shortcutArgs ?? {}
  switch (path) {
    case 'name':
      return plan?.insuranceProvider?.name
    default:
      return undefined
  }
}

export const getInsuranceValue = (path: string, shortcutArgs: ShortcutArgs): string | undefined => {
  // ex: payments.received.0
  const [, fieldPath, ...rest] = path.split('.')
  const { plan } = shortcutArgs ?? {}
  switch (fieldPath) {
    case 'memberId':
      return plan?.memberId
    case 'allowedCharges':
      return getAllowedChargesString(shortcutArgs)
    case 'reimbursement':
      return getReimbursementString(shortcutArgs)
    case 'inNetwork':
      return getNetworkValue(rest.join('.'), plan?.['in-network'])
    case 'outOfNetwork':
      return getNetworkValue(rest.join('.'), plan?.['out-of-network'])
    case 'policyOwner':
      return getPolicyOwnerValue(rest.join('.'), shortcutArgs)
    case 'insuranceProvider':
      return getInsurerValue(rest.join('.'), shortcutArgs)
    default:
      return undefined
  }
}
